import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import {
	GROUPED_ACTIVITY_LOG_DATE_FORMAT,
	GROUPED_ACTIVITY_LOG_PARAM,
	type GroupedActivityLogUrlState,
	groupedActivityLogUrlParamKeys,
	parseGroupedActivityLogUrlState,
	writeGroupedActivityLogUrlState,
} from "./groupedActivityLogUrlState";

/** How far back the date range reaches when a link doesn't say — unchanged from the pre-URL default. */
const DEFAULT_LOOKBACK = { months: 1 } as const;

export type FilterChanges = Partial<GroupedActivityLogUrlState> | ((current: GroupedActivityLogUrlState) => Partial<GroupedActivityLogUrlState>);
/** Every write states its history behavior: a discrete choice pushes (Back undoes it); typing and
 * time-picker scrubbing replace, or Back would step through every keystroke. */
export type WriteOptions = { replace: boolean };

export type UseGroupedActivityLogUrlStateResult = {
	/** Every filter except the search box — `urlState.search` is always empty here, see `urlSearch`. */
	urlState: GroupedActivityLogUrlState;
	/** The committed search (`q`). Kept out of `urlState` so its writes don't re-derive the filters. */
	urlSearch: string;
	/** The effective range: the URL's, or today's default until the effect below writes it. `null` only
	 * when neither exists — the department's timezone is unusable (reported, never written). */
	from: string | null;
	to: string | null;
	/** False while the router already shows another department's path but this page (keyed by the old
	 * department) hasn't remounted yet — nothing may be written or pruned in that window. */
	urlMatchesDepartment: boolean;
	updateFilters: (changes: FilterChanges, options: WriteOptions) => void;
	commit: (build: (current: URLSearchParams) => URLSearchParams, options: WriteOptions) => void;
};

/** Params written on every visit, so their presence alone says nothing about a shared filter. */
const ALWAYS_WRITTEN_PARAMS: ReadonlySet<string> = new Set([
	GROUPED_ACTIVITY_LOG_PARAM.FROM,
	GROUPED_ACTIVITY_LOG_PARAM.TO,
	GROUPED_ACTIVITY_LOG_PARAM.PUNCHES,
]);

/**
 * Today's default range in the department's timezone, computed when asked rather than frozen, so a
 * long-open tab or a re-entry through the bare path gets today, not the day the page mounted.
 *
 * `null` for an unusable timezone: formatting would write "Invalid DateTime", which parses back as
 * missing, so the write effect would fire forever. The caller reports it and writes nothing.
 */
const defaultRange = (timezone: string): { from: string; to: string } | null => {
	const today = DateTime.now().setZone(timezone);
	if (!today.isValid) return null;
	return { from: today.minus(DEFAULT_LOOKBACK).toFormat(GROUPED_ACTIVITY_LOG_DATE_FORMAT), to: today.toFormat(GROUPED_ACTIVITY_LOG_DATE_FORMAT) };
};

/** The URL without the search box, so the box's own debounced writes don't re-derive any filter. */
const filterSignature = (params: URLSearchParams): string => {
	const copy = new URLSearchParams(params);
	copy.delete(GROUPED_ACTIVITY_LOG_PARAM.SEARCH);
	return copy.toString();
};

/**
 * `setSearchParams` builds on the params of the render it was created in, so two writes in one event
 * (the shift-hours Clear sets both times) would each start from the same URL and the second would
 * undo the first. The last write is remembered against the params it started from and used as the
 * base until the router re-renders with the new URL. Params and setter are read through refs so the
 * returned `commit` keeps one identity for the page's lifetime.
 */
const useUrlCommit = (): UseGroupedActivityLogUrlStateResult["commit"] => {
	const [searchParams, setSearchParams] = useSearchParams();
	const searchParamsRef = useRef(searchParams);
	searchParamsRef.current = searchParams;
	const setSearchParamsRef = useRef(setSearchParams);
	setSearchParamsRef.current = setSearchParams;
	const pendingWriteRef = useRef<{ base: URLSearchParams; next: URLSearchParams } | null>(null);
	return useCallback((build, { replace }) => {
		const rendered = searchParamsRef.current;
		const pending = pendingWriteRef.current;
		const current = pending && pending.base === rendered ? pending.next : rendered;
		const next = build(current);
		pendingWriteRef.current = { base: rendered, next };
		setSearchParamsRef.current(next, { replace });
	}, []);
};

/**
 * One `activity_log_opened_with_url_filters` event when the page mounts on a URL that already carries
 * Grouped parameters (a shared link, a reload, Back) — keys only, never the values. Waits until the URL
 * and the chosen department agree, so it never reports under the previous department's id.
 * `has_non_default_params` separates a real shared filter from the from/to/punches every visit writes.
 */
const useTrackLandingWithUrlFilters = (searchParams: URLSearchParams, departmentId: string, urlMatchesDepartment: boolean): void => {
	const { track } = useAnalytics();
	const trackedRef = useRef(false);
	const landingKeysRef = useRef(groupedActivityLogUrlParamKeys(searchParams));
	useEffect(() => {
		if (trackedRef.current || !urlMatchesDepartment) return;
		trackedRef.current = true;
		const paramKeys = landingKeysRef.current;
		if (paramKeys.length === 0) return;
		track("activity_log_opened_with_url_filters", {
			department_id: departmentId,
			param_keys: paramKeys.join(","),
			param_count: paramKeys.length,
			has_non_default_params: paramKeys.some((key) => !ALWAYS_WRITTEN_PARAMS.has(key)),
		});
	}, [track, departmentId, urlMatchesDepartment]);
};

/**
 * The Grouped Activity Log's URL state: `useSearchParams` is the single source of truth. Parses it,
 * writes it (`updateFilters` merges into the freshly parsed current params), and makes sure the URL
 * always carries a date range and the Punches choice so a copied link reproduces the view.
 *
 * `storedShowPunches` is the user's saved Punches preference, used only when the URL doesn't say.
 */
export function useGroupedActivityLogUrlState({ storedShowPunches }: { storedShowPunches: boolean }): UseGroupedActivityLogUrlStateResult {
	const { timezone, chosenDepartment } = useHospitalData();
	const { departmentId: urlDepartmentId } = useParams<{ departmentId: string }>();
	const [searchParams] = useSearchParams();
	const commit = useUrlCommit();
	const urlMatchesDepartment = urlDepartmentId === chosenDepartment.department_id;
	useTrackLandingWithUrlFilters(searchParams, chosenDepartment.department_id, urlMatchesDepartment);
	const { trackError } = useAnalytics();
	const reportedInvalidTimezoneRef = useRef(false);

	const signature = useMemo(() => filterSignature(searchParams), [searchParams]);
	const urlState = useMemo(
		() => parseGroupedActivityLogUrlState(new URLSearchParams(signature), { fallbackShowPunches: storedShowPunches }),
		[signature, storedShowPunches],
	);
	const punchesInUrl = searchParams.has(GROUPED_ACTIVITY_LOG_PARAM.PUNCHES);
	// DEFAULT: an absent `q` is an empty search box.
	const urlSearch = searchParams.get(GROUPED_ACTIVITY_LOG_PARAM.SEARCH) ?? "";

	const updateFilters = useCallback(
		(changes: FilterChanges, options: WriteOptions) => {
			commit((current) => {
				const state = parseGroupedActivityLogUrlState(current, { fallbackShowPunches: storedShowPunches });
				return writeGroupedActivityLogUrlState(current, { ...state, ...(typeof changes === "function" ? changes(state) : changes) });
			}, options);
		},
		[commit, storedShowPunches],
	);

	// DEFAULT: the page always has a date range — a link without one (or with a malformed one) gets the
	// last month. The render-time fallback and the value written are computed the same way, so the list
	// query sees one range, not two. With an unusable timezone there is no default at all (null), and the
	// list query falls back to the backend's own default window.
	const fallbackRange = defaultRange(timezone);
	const from = urlState.from ?? fallbackRange?.from ?? null;
	const to = urlState.to ?? fallbackRange?.to ?? null;
	useEffect(() => {
		if (!urlMatchesDepartment) return;
		if (urlState.from !== null && urlState.to !== null && punchesInUrl) return;
		const range = defaultRange(timezone);
		if (range === null) {
			// Never write: the unusable range would read back as missing and re-trigger this effect.
			if (!reportedInvalidTimezoneRef.current) {
				reportedInvalidTimezoneRef.current = true;
				trackError("activity_log_default_range_invalid_timezone", { department_id: chosenDepartment.department_id, timezone });
			}
			return;
		}
		updateFilters((current) => ({ from: current.from ?? range.from, to: current.to ?? range.to }), { replace: true });
	}, [urlMatchesDepartment, urlState.from, urlState.to, punchesInUrl, timezone, updateFilters, trackError, chosenDepartment.department_id]);

	return { urlState, urlSearch, from, to, urlMatchesDepartment, updateFilters, commit };
}
